import React from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { getBalanceNumber, getFullDisplayBalance } from 'utils/formatBalance'
import { useTranslation } from 'contexts/Localization'
import { Flex, MetamaskIcon, Text, LinkExternal, Skeleton, Button, Link } from 'vvs-uikit'
import { BASE_EXPLORER_URLS, CRONOS_BLOCK_TIME } from 'config'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useBlock } from 'state/block/hooks'
import { useVvsVault } from 'state/pools/hooks'
import { DeserializedPool } from 'state/types'
import { getAddress, getVvsVaultAddress } from 'utils/addressHelpers'
import { registerToken } from 'utils/wallet'
import { isBlindMode } from 'utils'
import Balance from 'components/Balance'
import { getPoolBlockInfo } from 'views/Pools/helpers'
import CountdownTooltip from 'components/CountdownTooltip'
import useActiveWeb3React from '../../../../../hooks/useActiveWeb3React'

dayjs.extend(duration)

interface ExpandedFooterProps {
  pool: DeserializedPool
  account: string
}

const ExpandedWrapper = styled(Flex)`
  svg {
    height: 14px;
    width: 14px;
  }
`

const StyledLinkExternal = styled(LinkExternal)`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.primary};
`

const ExpandedFooter: React.FC<ExpandedFooterProps> = ({ pool, account }) => {
  const { t } = useTranslation()
  const { chainId } = useActiveWeb3React()
  const { currentBlock } = useBlock()
  const {
    totalVvsInVault,
    fees: { performanceFee },
  } = useVvsVault()

  const { stakingToken, earningToken, totalStaked, stakingLimit, contractAddress, sousId, isAutoVault } = pool

  const tokenAddress = earningToken.address || ''
  const poolContractAddress = getAddress(contractAddress)
  const vvsVaultContractAddress = getVvsVaultAddress()
  const isMetaMaskInScope = !!window.ethereum?.isMetaMask
  const isManualVvsPool = sousId === 0

  const { shouldShowBlockCountdown, blocksUntilStart, blocksRemaining, hasPoolStarted, blocksToDisplay } =
    getPoolBlockInfo(pool, currentBlock)

  const getTotalStakedBalance = () => {
    if (isAutoVault) {
      return getBalanceNumber(totalVvsInVault, stakingToken.decimals)
    }
    if (isManualVvsPool) {
      const manualVvsTotalMinusAutoVault = new BigNumber(totalStaked).minus(totalVvsInVault)
      return getBalanceNumber(manualVvsTotalMinusAutoVault, stakingToken.decimals)
    }
    return getBalanceNumber(totalStaked, stakingToken.decimals)
  }

  return (
    <ExpandedWrapper flexDirection="column">
      <Flex mb="2px" justifyContent="space-between" alignItems="center">
        <Text small>{t('Total staked')}:</Text>
        <Flex alignItems="flex-start">
          {totalStaked && totalStaked.gte(0) ? (
            <Balance color="text" small value={getTotalStakedBalance()} decimals={0} unit={` ${stakingToken.symbol}`} />
          ) : (
            <Skeleton width="90px" height="21px" />
          )}
        </Flex>
      </Flex>
      {stakingLimit && stakingLimit.gt(0) && (
        <Flex mb="2px" justifyContent="space-between">
          <Text small>{t('Max. stake per user')}:</Text>
          <Text small>{`${getFullDisplayBalance(stakingLimit, stakingToken.decimals, 0)} ${stakingToken.symbol}`}</Text>
        </Flex>
      )}
      {shouldShowBlockCountdown && (
        <Flex mb="2px" justifyContent="space-between" alignItems="center">
          <Text small>{hasPoolStarted ? t('Ends in') : t('Starts in')}:</Text>
          {blocksRemaining || blocksUntilStart ? (
            <Link href={`${BASE_EXPLORER_URLS[chainId]}/block/${currentBlock + blocksToDisplay}`} target="_blank">
              <Balance small value={blocksToDisplay} decimals={0} color="primary" />
              <Text small ml="4px" color="primary" textTransform="lowercase">
                {t('Blocks')}
              </Text>
              <CountdownTooltip
                timestamp={blocksToDisplay * CRONOS_BLOCK_TIME * 1000}
                tooltipText={hasPoolStarted ? 'Approximately Ends in' : 'Approximately Starts in'}
              />
            </Link>
          ) : (
            <Skeleton width="54px" height="21px" />
          )}
        </Flex>
      )}
      {isAutoVault && (
        <Flex mb="2px" justifyContent="space-between" alignItems="center">
          <Text small>{t('Performance Fee')}:</Text>
          {/* {tooltipVisible && tooltip}
          <TooltipText ref={targetRef} small>
            {t('Performance Fee')}
          </TooltipText> */}
          <Flex alignItems="center">
            {performanceFee ? (
              <Text ml="4px" small>
                {performanceFee / 100}%
              </Text>
            ) : (
              <Skeleton width="90px" height="21px" />
            )}
          </Flex>
        </Flex>
      )}
      {!isBlindMode() && (
        <Flex mt={3} mb="2px" justifyContent="flex-start">
          <StyledLinkExternal href={`/info/token/${earningToken.address}`} bold={false} small>
            {t('See Token Info')}
          </StyledLinkExternal>
        </Flex>
      )}
      <Flex mb="2px" justifyContent="flex-start">
        <StyledLinkExternal href={earningToken.projectLink} bold={false} small>
          {t('View Project Site')}
        </StyledLinkExternal>
      </Flex>
      {poolContractAddress && (
        <Flex mb="2px" justifyContent="flex-start">
          <StyledLinkExternal
            href={`${BASE_EXPLORER_URLS[chainId]}/address/${
              isAutoVault ? vvsVaultContractAddress : poolContractAddress
            }`}
            bold={false}
            small
          >
            {t('View Contract')}
          </StyledLinkExternal>
        </Flex>
      )}
      {account && isMetaMaskInScope && tokenAddress && (
        <Flex justifyContent="flex-start">
          <Button
            variant="text"
            p="0"
            height="auto"
            onClick={() => registerToken(tokenAddress, earningToken.symbol, earningToken.decimals)}
          >
            <Text color="primary" fontSize="14px">
              {t('Add to Metamask')}
            </Text>
            <MetamaskIcon ml="4px" />
          </Button>
        </Flex>
      )}
    </ExpandedWrapper>
  )
}

export default React.memo(ExpandedFooter)
